import * as React from 'react';

import {Button} from 'shells/button/button';
import {UpsellButton} from 'shells/button/upsell-button/upsell-button';
import {SectionAddItemButton} from 'shells/lead-sidebar/section-add-item-button';
import {AddChannelSelect} from 'shells/lead-sidebar/sources/add-channel-select';
import {SourcesSection} from 'shells/lead-sidebar/sources/sources-section';
import {SourcesSectionContent} from 'shells/lead-sidebar/sources/sources-section-content';
import {SourcesSelect} from 'shells/lead-sidebar/sources/sources-select';
import {SectionLabel} from 'shells/typography/section-label';

import type {ChannelType} from 'nutshell-graphql-types';

import {useDismissibleUserSetting} from '../../../util/hooks';

import {GetSessionUser} from '../../core/get-session-user';
import {useOpenableModal, OpenableModals} from '../../openable-modals';
import {SOURCES_WRAPPER_LIMIT} from '../../setup/sources/helpers';
import {useGetSources} from '../../setup/sources/use-get-sources';

import {getAddableSources} from './helpers';
import {
    useAddSource,
    useGetLeadChannels,
    useAddChannel,
    useRemoveSource,
    useRemoveChannel,
} from './hooks';

import styles from './sources-section-wrapper.module.css';

type Props = {
    model: any;
};

export function SourcesSectionWrapper(props: Props) {
    const [isAddingChannel, setIsAddingChannel] = React.useState<boolean>(false);
    const [isAddingSource, setIsAddingSource] = React.useState<boolean>(false);
    const {shouldShow} = useDismissibleUserSetting('has_dismissed_adtech_fx_modal');
    const {onOpen: onOpenAdTechUpsell} = useOpenableModal(OpenableModals.ADTECH_FX_INTEREST_MODAL, {
        canDismiss: true,
    });

    const leadId = props.model.id;
    const shouldShowAdTechUpsell = shouldShow && NutClientConfig.hasAdTechFxUpsell;

    const {channels, additionalSources, refetch} = useGetLeadChannels(leadId);
    const {sources} = useGetSources({limit: SOURCES_WRAPPER_LIMIT});
    const addSource = useAddSource();
    const addChannel = useAddChannel();
    const removeSource = useRemoveSource();
    const removeChannel = useRemoveChannel();

    const handleAddSource = (sourceId: string) => {
        addSource({leadId, sourceId}).then(() => {
            refetch();
        });
        // Fake delay while saving
        setTimeout(() => {
            setIsAddingSource(false);
        }, 1000);
    };

    const handleAddChannel = (channel: ChannelType | 'NO_CHANNEL') => {
        addChannel({leadId, channel}).then(() => {
            refetch();
        });
        // Fake delay while saving
        setTimeout(() => {
            setIsAddingChannel(false);
        }, 1000);
    };

    const handleRemoveSource = (sourceId: string) => {
        removeSource({leadId, sourceId}).then(() => refetch());
    };

    const handleRemoveChannel = (channel?: ChannelType | null): Promise<any> => {
        return removeChannel({leadId, channel}).then(() => refetch());
    };

    return (
        <GetSessionUser>
            {({user}) => (
                <div className={styles['section-container']}>
                    <SectionLabel>
                        Sources
                        <div className={styles['button-container']}>
                            <Button variant='text-primary' onClick={() => setIsAddingChannel(true)}>
                                + Add a channel
                            </Button>
                        </div>
                    </SectionLabel>
                    {isAddingChannel ? (
                        <div className={styles['content-container']}>
                            <AddChannelSelect
                                existingChannels={channels.map((channel) => channel.node.value)}
                                onSelectChannel={handleAddChannel}
                                onClickRemove={() => setIsAddingChannel(false)}
                                onBlur={() => setIsAddingChannel(false)}
                            />
                        </div>
                    ) : undefined}
                    {additionalSources.length ? (
                        <div className={styles['content-container']}>
                            <SourcesSectionContent
                                channel={null}
                                sources={additionalSources}
                                sourceOptions={getAddableSources(
                                    sources,
                                    channels,
                                    additionalSources,
                                    null
                                )}
                                onRemoveSource={(sourceId: string) => handleRemoveSource(sourceId)}
                                onAddSource={(sourceId: string) => handleAddSource(sourceId)}
                                canAccessSetup={Boolean(user && user.permissions.canAccessSetup)}
                            />
                        </div>
                    ) : undefined}
                    {channels.map((channel) => (
                        <div key={channel.node.value} className={styles['content-container']}>
                            <SourcesSection
                                channel={channel.node.value}
                                sources={channel.sources}
                                sourceOptions={getAddableSources(
                                    sources,
                                    channels,
                                    additionalSources,
                                    channel.node.value
                                )}
                                onRemoveChannel={() => handleRemoveChannel(channel.node.value)}
                                onRemoveSource={(sourceId: string) => handleRemoveSource(sourceId)}
                                onAddSource={(sourceId: string) => handleAddSource(sourceId)}
                                canAccessSetup={Boolean(user && user.permissions.canAccessSetup)}
                            />
                        </div>
                    ))}
                    <SectionAddItemButton
                        sectionEntity='sources'
                        isAddingItem={isAddingSource}
                        setIsAddingItem={setIsAddingSource}
                        addItemComponent={
                            <SourcesSelect
                                sourceOptions={getAddableSources(
                                    sources,
                                    channels,
                                    additionalSources
                                )}
                                onBlur={() => setIsAddingSource(false)}
                                onSelectSource={(sourceId: string) => handleAddSource(sourceId)}
                                canAccessSetup={Boolean(user && user.permissions.canAccessSetup)}
                            />
                        }
                    />
                    {shouldShowAdTechUpsell && (
                        <div className='full-width mt-8'>
                            <UpsellButton
                                onClick={onOpenAdTechUpsell}
                                text='Advertise to this lead'
                            />
                        </div>
                    )}
                </div>
            )}
        </GetSessionUser>
    );
}
